/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { createContext, useContext, useMemo, useState, useCallback, useRef } from 'react';
const AIAgentMessageContext = createContext(null);
export function useAIAgentMessage() {
    const ctx = useContext(AIAgentMessageContext);
    if (!ctx) {
        throw new Error('useAIAgentMessage must be used within <AIAgentMessageProvider>');
    }
    return ctx;
}
export function useOptionalAIAgentMessage() {
    return useContext(AIAgentMessageContext);
}
export function AIAgentMessageProvider({ children, agent, envelope, onConfirm, onCancel, onAction, onUndo, frozen = false }) {
    // Track only user-initiated clicks; the frozen prop drives the disabled state directly.
    // useState(frozen) would only init once on mount — if frozen transitions true→false after
    // pendingApprovalId arrives, the buttons would never re-enable (IP-14201 follow-up).
    const [userActionTaken, setUserActionTaken] = useState(false);
    const actionTakenRef = useRef(false);
    const frozenRef = useRef(frozen);
    frozenRef.current = frozen;
    const isActionTaken = frozen || userActionTaken;
    const wrappedOnConfirm = useCallback((approvalId) => {
        if (frozenRef.current || actionTakenRef.current)
            return;
        actionTakenRef.current = true;
        setUserActionTaken(true);
        onConfirm === null || onConfirm === void 0 ? void 0 : onConfirm(approvalId);
    }, [onConfirm]);
    const wrappedOnCancel = useCallback((approvalId) => {
        if (frozenRef.current || actionTakenRef.current)
            return;
        actionTakenRef.current = true;
        setUserActionTaken(true);
        onCancel === null || onCancel === void 0 ? void 0 : onCancel(approvalId);
    }, [onCancel]);
    const value = useMemo(() => ({
        agent,
        envelope,
        onConfirm: wrappedOnConfirm,
        onCancel: wrappedOnCancel,
        onAction,
        onUndo,
        isActionTaken
    }), [agent, envelope, wrappedOnConfirm, wrappedOnCancel, onAction, onUndo, isActionTaken]);
    return React.createElement(AIAgentMessageContext.Provider, { value: value }, children);
}
